import { Box, Divider, Grid, Typography, useTheme } from "@mui/material";
import { durationDisplay } from "components/SoundClipEditor/Controls/Player";
import SoundClipEditor from "components/SoundClipEditor/SoundClipEditor";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import { observer } from "mobx-react";
import moment from "moment";
import { AuthStore } from "stores/AuthStore";
import { standardDateFormat } from "utils/DateTimeUtils";
import { useStore } from "utils/useStore";
import { SignalsAudioStatus } from "../../SignalsModels";
import { MontageCardMenu } from "./MontageCardMenu";
import MontageCardStore from "./MontageCardStore";
import { defaultShadowsForSignals } from "components/Signals/SignalsUtils";

const MontageCard = observer(({ store }: { store: MontageCardStore }) => {
    const theme = useTheme();
    const authStore = useStore(AuthStore);

    const isLoading = store.getTaskLoading("refresh");
    const hasError = !!store.error;
    const montageStatus = store.data?.status;
    const clip = store.data?.clip;
    const displayTitle = store.title?.trim() || store.data?.audioName || "";

    const userCannotViewMedia =
        !authStore.isUserUltra() && authStore.canUserView("Restrict Media");

    if (
        userCannotViewMedia ||
        montageStatus === SignalsAudioStatus.DoesNotHaveAccess
    ) {
        return null;
    }

    // The empty states are a few pixels taller than the card's two rows, and
    // a montage's height follows its content, so on their own they grow the
    // card to three rows until the clip loads. Filling the card instead keeps
    // its measured height at the size it already has.
    const fillCard = (state: React.ReactNode) => (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
            }}
        >
            {state}
        </Box>
    );

    const renderBody = () => {
        if (isLoading) {
            return fillCard(<AcxEmptyState variant="loading" size="small" />);
        }

        if (hasError) {
            return fillCard(
                <AcxEmptyState
                    variant="error"
                    size="small"
                    overrideSubTitle={store.error ?? undefined}
                />,
            );
        }

        if (!store.data) {
            return fillCard(<AcxEmptyState variant="loading" size="small" />);
        }

        if (store.data.status !== SignalsAudioStatus.HasAccess || !clip) {
            return (
                <Grid container padding={theme.spacing(3)}>
                    <Grid item xs={12} textAlign="center">
                        {store.data.status === SignalsAudioStatus.Deleted
                            ? "Montage has been deleted."
                            : "No access to this montage."}
                    </Grid>
                </Grid>
            );
        }

        return (
            <Grid container item xs={12}>
                <Grid
                    container
                    item
                    xs={12}
                    wrap="nowrap"
                    padding={theme.spacing(2)}
                    paddingLeft={theme.spacing(3)}
                    paddingRight={theme.spacing(3)}
                    justifyContent={"space-between"}
                >
                    {/* The menu keeps its width; the title takes the rest. */}
                    <Grid
                        container
                        item
                        alignItems={"center"}
                        wrap="nowrap"
                        sx={{
                            flex: "1 1 auto",
                            minWidth: 0,
                            overflow: "hidden",
                        }}
                    >
                        <Typography
                            fontWeight={600}
                            fontSize="16px"
                            paddingRight={theme.spacing(1)}
                        >
                            {displayTitle}
                        </Typography>
                        <Typography
                            fontWeight={400}
                            fontSize="14px"
                            color={theme.palette.gray[400]}
                        >
                            {durationDisplay(store.data.duration ?? 0)}
                            {" | "}
                            {standardDateFormat(
                                moment(store.data.createdOn),
                                "MM/DD/YYYY",
                            )}
                        </Typography>
                    </Grid>
                    {!store.isPresentationMode &&
                        (store.isEditable || store.isDeletable) && (
                            <MontageCardMenu store={store} />
                        )}
                </Grid>
                <Grid item xs={12}>
                    <Divider />
                </Grid>
                <Grid
                    container
                    item
                    xs={12}
                    padding={theme.spacing(2)}
                    paddingLeft={theme.spacing(3)}
                    paddingRight={theme.spacing(3)}
                >
                    <SoundClipEditor
                        id={`signals-montage-${store.montageId}`}
                        key={`signals-montage-${store.montageId}-${
                            store.isPresentationMode ? "p" : "d"
                        }`}
                        noMenu
                        clip={clip}
                        downloadMediaFileId={store.montageId}
                        downloadMediaSourceType="montage"
                        loadingZIndex={1097}
                    />
                </Grid>
            </Grid>
        );
    };

    // SoundClipEditor refits the waveform to its own container.
    return (
        <Grid
            sx={{
                // No margin: the dashboard grid supplies the gap between cards.
                width: "100%",
                height: "100%",
                borderRadius: 2,
                backgroundColor: theme.palette.white.main,
                boxShadow: defaultShadowsForSignals,
            }}
            id={store.id}
        >
            {renderBody()}
        </Grid>
    );
});

export default MontageCard;
